'use client';

import * as React from 'react';
import { Check, LucideIcon } from 'lucide-react';

import { cn } from '../lib/utils';
import { Progress } from '../shadcn/progress';
import { Trans } from './trans';

export interface WizardStep {
  /**
   * Unique identifier for the step
   */
  id: string;

  /**
   * Display label for the step
   */
  label: string;

  /**
   * Optional description
   */
  description?: string;

  /**
   * Lucide icon component for the step
   */
  icon: LucideIcon;
}

export type StepStatus = 'default' | 'saved' | 'warning';

export interface WizardProgressProps {
  /**
   * Current step index (0-based)
   */
  currentStep: number;

  /**
   * Total number of steps (required for 'bar' and 'dots' variants)
   */
  totalSteps?: number;

  /**
   * Optional step title to display (for 'bar' variant)
   */
  stepTitle?: string;

  /**
   * Step configuration (required for 'icons' variant)
   */
  steps?: WizardStep[];

  /**
   * Display variant:
   * - 'bar': Linear progress bar with step counter (default)
   * - 'dots': Dot indicators
   * - 'icons': Circles with icons and connector lines
   */
  variant?: 'bar' | 'dots' | 'icons';

  /**
   * Whether to show step labels below icons (for 'icons' variant)
   * @default true
   */
  showLabels?: boolean;

  /**
   * Per-step status for completion indicators (for 'icons' variant).
   * 'saved' = green checkmark, 'warning' = amber indicator, 'default' = normal.
   * When provided, overrides the default "completed if before current step" logic.
   */
  stepStatuses?: StepStatus[];

  /**
   * Click handler for step icons (for 'icons' variant).
   * When provided, step icons render as buttons and the user can jump
   * directly to a step. Use `isStepClickable` to gate which steps are
   * reachable (e.g. only steps already saved).
   */
  onStepClick?: (index: number) => void;

  /**
   * Predicate that decides whether a step icon is interactive.
   * Only consulted when `onStepClick` is provided.
   * Defaults to "any step is clickable" when omitted.
   */
  isStepClickable?: (index: number) => boolean;

  /**
   * Optional className
   */
  className?: string;
}

/**
 * Progress indicator for wizard/stepper flows.
 * Supports bar (default), dots, and icons variants.
 *
 * @example
 * ```tsx
 * // Bar variant (default)
 * <WizardProgress
 *   currentStep={0}
 *   totalSteps={5}
 *   stepTitle="Overview"
 * />
 *
 * // Icons variant with step icons and connector lines
 * <WizardProgress
 *   variant="icons"
 *   currentStep={1}
 *   steps={[
 *     { id: 'address', label: 'Address', icon: Building },
 *     { id: 'tax', label: 'Tax Info', icon: FileText },
 *     { id: 'bank', label: 'Bank Details', icon: CreditCard },
 *   ]}
 * />
 * ```
 */
export function WizardProgress({
  currentStep,
  totalSteps: totalStepsProp,
  stepTitle,
  steps,
  variant = 'bar',
  showLabels = true,
  stepStatuses,
  onStepClick,
  isStepClickable,
  className,
}: WizardProgressProps) {
  // For icons variant, derive totalSteps from steps array
  const totalSteps = totalStepsProp ?? steps?.length ?? 0;
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  // Icons variant: circles with icons and connector lines
  if (variant === 'icons' && steps) {
    return (
      <div
        className={cn('flex items-center justify-between py-4', className)}
        role="progressbar"
        aria-valuenow={currentStep + 1}
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-label={`Step ${currentStep + 1} of ${steps.length}: ${steps[currentStep]?.label}`}
      >
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const status = stepStatuses?.[index];
          const isSaved = status === 'saved';
          const isWarning = status === 'warning';
          // When stepStatuses is provided, use it; otherwise fall back to position-based logic
          const isCompleted = stepStatuses
            ? isSaved && index !== currentStep
            : index < currentStep;
          const isCurrent = index === currentStep;
          const clickable =
            !!onStepClick &&
            !isCurrent &&
            (isStepClickable ? isStepClickable(index) : true);

          const iconClasses = cn(
            'relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
            isCurrent && 'border-primary bg-primary text-primary-foreground',
            isCompleted && 'border-primary bg-primary/10 text-primary',
            isWarning &&
              !isCurrent &&
              'border-amber-500 bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
            isSaved &&
              !isCurrent &&
              !isCompleted &&
              'border-green-500 bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
            !isCurrent &&
              !isCompleted &&
              !isSaved &&
              !isWarning &&
              'border-muted bg-background text-muted-foreground',
            clickable &&
              'hover:border-primary hover:text-primary focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          );

          const iconContent =
            isCompleted || (isSaved && !isCurrent) ? (
              <Check className="h-5 w-5" aria-hidden="true" />
            ) : (
              <StepIcon className="h-5 w-5" aria-hidden="true" />
            );

          return (
            <div
              key={step.id}
              className={cn(
                'flex flex-1 flex-col items-center',
                index < steps.length - 1 && 'relative',
              )}
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'absolute top-5 left-[calc(50%+1.25rem)] z-0 h-0.5 w-[calc(100%-2.5rem)]',
                    isCompleted || isSaved
                      ? 'bg-primary'
                      : 'bg-muted-foreground/30',
                  )}
                  aria-hidden="true"
                />
              )}

              {/* Step icon circle (button when clickable) */}
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick?.(index)}
                  aria-label={`Go to step ${index + 1}: ${step.label}`}
                  className={iconClasses}
                >
                  {iconContent}
                </button>
              ) : (
                <div
                  className={iconClasses}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {iconContent}
                </div>
              )}

              {/* Step label (hidden on mobile) */}
              {showLabels && (
                <div
                  className={cn(
                    'mt-2 text-center text-xs',
                    isCurrent && 'text-foreground font-medium',
                    !isCurrent && 'text-muted-foreground',
                  )}
                >
                  <span className="hidden sm:block">{step.label}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Dots variant
  if (variant === 'dots') {
    return (
      <div
        className={cn('flex justify-center gap-2 py-3', className)}
        role="progressbar"
        aria-valuenow={currentStep + 1}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        aria-label={`Step ${currentStep + 1} of ${totalSteps}`}
      >
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={cn(
              'h-2.5 w-2.5 rounded-full transition-colors',
              index <= currentStep ? 'bg-primary' : 'bg-muted',
            )}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  // Default: bar variant
  return (
    <div className={cn('py-3', className)}>
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          <Trans
            i18nKey="common:stepOf"
            defaults="Step {{ current }} of {{ total }}"
            values={{ current: currentStep + 1, total: totalSteps }}
          />
        </span>
        {stepTitle && <span className="font-medium">{stepTitle}</span>}
      </div>
      <Progress
        value={progress}
        className="h-2"
        aria-label={`Step ${currentStep + 1} of ${totalSteps}${stepTitle ? `: ${stepTitle}` : ''}`}
      />
    </div>
  );
}
